import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { configService } from '../services/configService';
import FeatureEngagementModal from './FeatureEngagementModal';
import CountryAvailabilityModal from './CountryAvailabilityModal';
import BetaAvailabilityModal from './BetaAvailabilityModal';
import { useCountryAvailability } from '../hooks/useCountryAvailability';
import { useBetaAvailability } from '../hooks/useBetaAvailability';
import Logo from './Logo';

function Plans() {
  const navigate = useNavigate();
  const { user, updateUserProfile, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTocModal, setShowTocModal] = useState(false);
  const [tocAccepted, setTocAccepted] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [engagementModal, setEngagementModal] = useState({ isOpen: false, plan: null });
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const { showCountryModal, countryCode, userEmail, closeModal } = useCountryAvailability();
  const { showBetaModal, closeBetaModal } = useBetaAvailability();

  useEffect(() => {
    checkSubscriptionAndRedirect();
  }, [user]);

  const checkSubscriptionAndRedirect = async () => {
    console.log('🔍 Plans: checkSubscriptionAndRedirect called, user:', !!user);
    
    if (!user) {
      console.log('🔍 Plans: No user, skipping subscription check');
      setCheckingSubscription(false);
      return;
    }
    
    // Check email verification status (skip for OAuth users)
    const isOAuthUser = user?.username?.includes('Google_') || user?.username?.includes('Facebook_') || user?.username?.includes('LoginWithAmazon_') || user?.username?.includes('SignInWithApple_');
    const emailVerified = user?.attributes?.email_verified;
    
    if (!isOAuthUser && (emailVerified === false || emailVerified === 'false')) {
      console.log('🔍 Plans: Email/password user not verified, redirecting to login');
      navigate('/login');
      return;
    } else if (isOAuthUser) {
      console.log('🔍 Plans: OAuth user detected, skipping email verification check');
    }

    try {
      console.log('🔍 Plans: Calling getSubscriptionStatus...');
      const status = await configService.getSubscriptionStatus();
      console.log('🔍 Plans: Subscription status received:', status);
      setSubscriptionStatus(status);
      
      // If user has active subscription or trial, redirect to dashboard
      if (status?.status === 'active' || status?.status === 'trialing') {
        console.log('✅ Plans: Valid subscription detected, redirecting to dashboard. Status:', status?.status);
        navigate('/dashboard', { replace: true });
        return;
      } else {
        console.log('📋 Plans: No valid subscription, staying on plans page. Status:', status?.status);
      }
    } catch (error) {
      console.warn('❌ Plans: Could not check subscription status:', error);
    } finally {
      setCheckingSubscription(false);
    }

    // Only load plans if no active subscription
    console.log('🔍 Plans: Loading plans and TOC check...');
    const accepted = localStorage.getItem('tocAccepted');
    if (accepted === 'true') {
      setTocAccepted(true);
    } else {
      setShowTocModal(true);
    }
    loadPlans();
  };

  const loadPlans = async () => {
    try {
      const response = await configService.getSubscriptionProducts();
      setPlans(response.products);
    } catch (error) {
      console.error('Failed to load plans:', error);
      setError('Failed to load subscription plans. Please try again.');
    } finally {
      setLoadingPlans(false);
    }
  };

  // Show loading while checking subscription
  if (checkingSubscription) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-gray-600">Checking subscription status...</div>
      </div>
    );
  }

  // Filter to only monthly and annual plans, then sort by display_order metadata
  const sortedPlans = plans
    .filter(p => p.interval === 'month' || p.interval === 'year')
    .sort((a, b) => {
      const orderA = parseInt(a.display_order) || 999;
      const orderB = parseInt(b.display_order) || 999;
      return orderA - orderB;
    });

  const handleTocAgree = () => {
    localStorage.setItem('tocAccepted', 'true');
    setTocAccepted(true);
    setShowTocModal(false);
  };

  const handleTocCancel = () => {
    logout();
  };

  const selectPlan = async (plan) => {
    if (!tocAccepted) {
      setShowTocModal(true);
      return;
    }

    // Check if this is a waitlist product
    if (plan.metadata?.WaitList === 'True') {
      setEngagementModal({
        isOpen: true,
        plan: plan
      });
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // 1. Save plan selection immediately (before Stripe checkout)
      console.log('💾 Saving plan selection:', plan.price_id);
      await configService.selectSubscriptionPlan(plan.price_id);
      console.log('✅ Plan selection saved successfully');
      
      // 2. Create Stripe checkout session
      const checkoutData = {
        price_id: plan.price_id,
        success_url: `${window.location.origin}/setup?payment=success&plan=${plan.price_id}`,
        cancel_url: `${window.location.origin}/plans?payment=cancelled`,
        metadata: {
          cognito_user_id: user?.attributes?.sub  // Use Cognito user ID from JWT
        }
      };
      
      const response = await configService.createCheckoutSession(checkoutData);
      window.location.href = response.checkout_url;
    } catch (error) {
      console.error('Plan selection or checkout failed:', error);
      if (error.message.includes('business_id not found')) {
        setError('Please complete your business setup first. Redirecting to setup...');
        setTimeout(() => navigate('/setup'), 2000);
      } else {
        setError('Failed to select plan or start checkout. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const closeEngagementModal = () => {
    setEngagementModal({ isOpen: false, plan: null });
  };

  return (
    <>
      {showTocModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-2xl max-w-md w-full mx-4 shadow-xl">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Terms of Conditions Agreement</h2>
            <p className="text-gray-600 mb-4">Before selecting a plan, you must agree to our Terms of Conditions.</p>
            <p className="text-gray-600 mb-6">By clicking "Agree", you acknowledge that you have read and agree to be bound by our <Link to="/toc" target="_blank" className="text-primary hover:underline">Terms of Conditions</Link>.</p>
            <div className="flex gap-4">
              <button 
                className="flex-1 px-6 py-3 rounded-xl bg-primary hover:bg-primary-hover text-white font-semibold"
                onClick={handleTocAgree}
              >
                Agree
              </button>
              <button 
                className="flex-1 px-6 py-3 rounded-xl border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 font-semibold"
                onClick={handleTocCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      <nav className="flex justify-between items-center px-6 py-4 bg-white shadow-sm fixed top-0 w-full z-50">
        <div>
          <Link to="/" className="no-underline">
            <Logo height="32px" />
          </Link>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/dashboard" className="text-gray-700 hover:text-blue-500 no-underline">Dashboard</Link>
          <button 
            onClick={logout}
            className="text-blue-500 hover:text-blue-600 bg-transparent border-none cursor-pointer"
          >
            Sign Out
          </button>
        </div>
      </nav>

      <section className="pt-24 pb-16 px-6 text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Choose Your Plan
        </h1>
        <p className="text-lg text-gray-600 mb-8">
          All plans include 14-day free trial. Credit card required to start trial.
        </p>
        
        {error && (
          <div className="bg-red-100 text-red-600 p-4 rounded-xl mb-8 text-center max-w-2xl mx-auto">
            {error}
          </div>
        )}

        {subscriptionStatus?.status === 'active' && (
          <div className="bg-green-100 text-green-700 p-4 rounded-xl mb-8 text-center max-w-2xl mx-auto">
            ✅ You have an active subscription! <Link to="/dashboard" className="text-blue-500 hover:underline">Go to Dashboard</Link>
          </div>
        )}

        {loadingPlans ? (
          <div className="text-center py-8">
            <div className="text-gray-600">Loading subscription plans...</div>
          </div>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
            {sortedPlans.map((plan, index) => (
              <div key={plan.price_id} className={`bg-white p-8 rounded-2xl shadow-sm border relative ${index === 1 ? 'border-blue-500 scale-105' : 'border-gray-200'}`}>
                {index === 1 && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </div>
                )}
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.name}</h3>
                <div className="mb-6">
                  <div className="text-4xl font-bold text-gray-900">
                    ${(plan.amount / 100).toFixed(0)}
                    <span className="text-lg font-medium text-gray-500">/{plan.interval === 'month' ? 'mo' : 'yr'}</span>
                  </div>
                  {plan.interval === 'year' && (
                    <div className="text-sm text-gray-600 mt-1">
                      ${((plan.amount / 100) / 12).toFixed(0)}/mo billed annually
                    </div>
                  )}
                </div>
                <p className="text-gray-600 mb-6 italic">
                  {plan.description || 'Professional AI assistant service'}
                </p>
                <ul className="space-y-3 mb-8">
                  {(plan.features?.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="text-accent font-bold">✓</span>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  )) || [
                    <li key="1" className="flex items-start gap-3">
                      <span className="text-accent font-bold">✓</span>
                      <span className="text-gray-700">Answer phone calls</span>
                    </li>,
                    <li key="2" className="flex items-start gap-3">
                      <span className="text-accent font-bold">✓</span>
                      <span className="text-gray-700">Book appointments</span>
                    </li>,
                    <li key="3" className="flex items-start gap-3">
                      <span className="text-accent font-bold">✓</span>
                      <span className="text-gray-700">Take booking deposits</span>
                    </li>,
                    <li key="4" className="flex items-start gap-3">
                      <span className="text-accent font-bold">✓</span>
                      <span className="text-gray-700">SMS text responses</span>
                    </li>,
                    <li key="5" className="flex items-start gap-3">
                      <span className="text-accent font-bold">✓</span>
                      <span className="text-gray-700">Call escalation alerts</span>
                    </li>
                  ])}
                </ul>
                <button 
                  className={`w-full px-6 py-4 rounded-xl font-semibold shadow transition-colors ${
                    index === 1 
                      ? 'bg-blue-500 hover:bg-blue-600 text-white' 
                      : 'bg-white border border-gray-300 text-gray-800 hover:bg-gray-50'
                  } ${loading ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                  onClick={() => selectPlan(plan)}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : `Choose ${plan.name}`}
                </button>
              </div>
            ))}
          </div>
        )}

        {!loadingPlans && sortedPlans.length > 0 && (
          <div className="mt-16 bg-gradient-to-br from-blue-500 to-blue-600 text-white p-12 rounded-2xl max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Your Free Trial Includes:</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              <div className="bg-white bg-opacity-10 p-4 rounded-xl text-center">
                ✓ Full access to all features
              </div>
              <div className="bg-white bg-opacity-10 p-4 rounded-xl text-center">
                ✓ Personal setup call
              </div>
              <div className="bg-white bg-opacity-10 p-4 rounded-xl text-center">
                ✓ No charges for 14 days
              </div>
              <div className="bg-white bg-opacity-10 p-4 rounded-xl text-center">
                ✓ Cancel anytime
              </div>
            </div>
          </div>
        )}
      </section>
      
      <FeatureEngagementModal
        isOpen={engagementModal.isOpen}
        onClose={closeEngagementModal}
        featureType="subscription_plan"
        featureId={engagementModal.plan?.id}
        featureName={engagementModal.plan?.name}
        context={{
          page: 'plans',
          location: 'pricing_card',
          price_id: engagementModal.plan?.price_id
        }}
      />
      
      <CountryAvailabilityModal 
        isOpen={showCountryModal}
        onClose={closeModal}
        countryCode={countryCode}
        email={userEmail}
      />
      
      <BetaAvailabilityModal 
        isOpen={showBetaModal}
        onClose={closeBetaModal}
        email={user?.attributes?.email}
      />
    </>
  );
}

export default Plans;